/**
 * ====================================================================
 * FIKO CAD SMART LABEL PLACER
 * ====================================================================
 *
 * Akıllı label yerleştirme sistemi
 * Overlap detection, occlusion avoidance, auto-rotation
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { SmartPlacementConfig } from '../visual/DimensionVisualStyle';
import { Dimension, DimensionBounds } from '../DimensionTypes';

// ====================================================================
// TYPES
// ====================================================================

/**
 * Label bounds (screen space)
 */
export interface LabelBounds {
    id: string;
    min: { x: number; y: number };
    max: { x: number; y: number };
    center: { x: number; y: number };
    width: number;
    height: number;
}

/**
 * Label placement result
 */
export interface LabelPlacement {
    position: THREE.Vector3;
    rotation: number;
    offset: { x: number; y: number };
    flipped: boolean;
    score: number;
}

/**
 * Quadtree node
 */
interface QuadtreeNode {
    bounds: { x: number; y: number; width: number; height: number };
    children: QuadtreeNode[] | null;
    items: LabelBounds[];
    depth: number;
}

// ====================================================================
// QUADTREE
// ====================================================================

/**
 * Quadtree - Spatial indexing for overlap detection
 */
class Quadtree {
    private root: QuadtreeNode;
    private maxDepth: number;
    private maxItems: number;

    constructor(
        bounds: { width: number; height: number },
        maxDepth: number = 6,
        maxItems: number = 4
    ) {
        this.maxDepth = maxDepth;
        this.maxItems = maxItems;
        this.root = {
            bounds: { x: 0, y: 0, width: bounds.width, height: bounds.height },
            children: null,
            items: [],
            depth: 0
        };
    }

    /**
     * Label ekle
     */
    insert(label: LabelBounds): void {
        this.insertIntoNode(this.root, label);
    }

    /**
     * Node'a ekle
     */
    private insertIntoNode(node: QuadtreeNode, label: LabelBounds): void {
        // Children varsa, uygun child'a ekle
        if (node.children) {
            const index = this.getIndex(node, label);
            if (index !== -1) {
                this.insertIntoNode(node.children[index], label);
                return;
            }
        }

        // Bu node'a ekle
        node.items.push(label);

        // Bölme gerekiyor mu?
        if (node.items.length > this.maxItems && node.depth < this.maxDepth && !node.children) {
            this.subdivide(node);

            // Mevcut öğeleri yeniden dağıt
            const items = [...node.items];
            node.items = [];

            for (const item of items) {
                const index = this.getIndex(node, item);
                if (index !== -1) {
                    this.insertIntoNode(node.children![index], item);
                } else {
                    node.items.push(item);
                }
            }
        }
    }

    /**
     * Bölme (subdivide)
     */
    private subdivide(node: QuadtreeNode): void {
        const { x, y, width, height } = node.bounds;
        const hw = width / 2;
        const hh = height / 2;

        node.children = [
            { bounds: { x: x + hw, y: y, width: hw, height: hh }, children: null, items: [], depth: node.depth + 1 },      // NE
            { bounds: { x: x, y: y, width: hw, height: hh }, children: null, items: [], depth: node.depth + 1 },           // NW
            { bounds: { x: x, y: y + hh, width: hw, height: hh }, children: null, items: [], depth: node.depth + 1 },      // SW
            { bounds: { x: x + hw, y: y + hh, width: hw, height: hh }, children: null, items: [], depth: node.depth + 1 }  // SE
        ];
    }

    /**
     * Hangi quadrant'a ait?
     */
    private getIndex(node: QuadtreeNode, label: LabelBounds): number {
        const { x, y, width, height } = node.bounds;
        const midX = x + width / 2;
        const midY = y + height / 2;

        const inTop = label.max.y < midY;
        const inBottom = label.min.y > midY;
        const inLeft = label.max.x < midX;
        const inRight = label.min.x > midX;

        if (inTop) {
            if (inRight) return 0; // NE
            if (inLeft) return 1;  // NW
        } else if (inBottom) {
            if (inLeft) return 2;  // SW
            if (inRight) return 3; // SE
        }

        return -1; // Birden fazla quadrant'a yayılıyor
    }

    /**
     * Sorgu - verilen bounds ile çakışanları bul
     */
    query(bounds: LabelBounds): LabelBounds[] {
        const result: LabelBounds[] = [];
        this.queryNode(this.root, bounds, result);
        return result;
    }

    /**
     * Node sorgula
     */
    private queryNode(node: QuadtreeNode, bounds: LabelBounds, result: LabelBounds[]): void {
        // Bu node'un bounds'u ile kesişiyor mu?
        if (!this.intersects(node.bounds, bounds)) {
            return;
        }

        // Bu node'daki öğeleri kontrol et
        for (const item of node.items) {
            if (this.labelsOverlap(item, bounds)) {
                result.push(item);
            }
        }

        // Children'ları kontrol et
        if (node.children) {
            for (const child of node.children) {
                this.queryNode(child, bounds, result);
            }
        }
    }

    /**
     * Kesişim kontrolü
     */
    private intersects(
        nodeBounds: { x: number; y: number; width: number; height: number },
        label: LabelBounds
    ): boolean {
        return !(
            label.min.x > nodeBounds.x + nodeBounds.width ||
            label.max.x < nodeBounds.x ||
            label.min.y > nodeBounds.y + nodeBounds.height ||
            label.max.y < nodeBounds.y
        );
    }

    /**
     * Label overlap kontrolü
     */
    private labelsOverlap(a: LabelBounds, b: LabelBounds): boolean {
        if (a.id === b.id) return false;

        return !(
            a.max.x < b.min.x ||
            a.min.x > b.max.x ||
            a.max.y < b.min.y ||
            a.min.y > b.max.y
        );
    }

    /**
     * Temizle
     */
    clear(): void {
        this.root.items = [];
        this.root.children = null;
    }
}

// ====================================================================
// SMART LABEL PLACER
// ====================================================================

/**
 * Smart Label Placer - Akıllı label yerleştirme
 */
export class SmartLabelPlacer {
    // Quadtree for spatial indexing
    private quadtree: Quadtree;

    // Mevcut label'lar
    private labels = new Map<string, LabelBounds>();

    // Konfigürasyon
    private config: SmartPlacementConfig;

    // Viewport boyutları
    private viewport: { width: number; height: number };

    // Pre-allocated vectors
    private readonly _tempVec3 = new THREE.Vector3();
    private readonly _tempVec4 = new THREE.Vector4();

    constructor(
        viewport?: { width: number; height: number },
        config?: Partial<SmartPlacementConfig>
    ) {
        // Varsayılan viewport (1920x1080)
        this.viewport = viewport || { width: 1920, height: 1080 };
        this.quadtree = new Quadtree(this.viewport);

        this.config = {
            enabled: true,
            avoidOverlap: true,
            avoidOcclusion: true,
            preferredDirections: ['up', 'right', 'down', 'left'],
            maxSearchSteps: 15,
            stepSize: 12,
            autoRotateText: true,
            keepTextUpright: true,
            ...config
        };
    }

    /**
     * Konfigürasyonu güncelle
     */
    setConfig(config: Partial<SmartPlacementConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Viewport'u güncelle
     */
    updateViewport(width: number, height: number): void {
        this.viewport = { width, height };
        this.quadtree = new Quadtree(this.viewport);

        // Mevcut label'ları yeniden ekle
        this.labels.forEach(label => {
            this.quadtree.insert(label);
        });
    }

    /**
     * Label yerleştir
     */
    placeLabel(
        dimension: Dimension,
        preferredPosition?: THREE.Vector3,
        labelSize?: { width: number; height: number },
        camera?: THREE.Camera
    ): LabelPlacement {
        // Varsayılan pozisyon
        const pos = preferredPosition || new THREE.Vector3();
        const size = labelSize || { width: 50, height: 20 };

        if (!this.config.enabled) {
            return {
                position: pos.clone(),
                rotation: 0,
                offset: { x: 0, y: 0 },
                flipped: false,
                score: 1
            };
        }

        const dimensionId = dimension.userData.dimensionId;

        // Screen space'e çevir
        const screenPos = this.worldToScreen(preferredPosition, camera);

        // Label bounds hesapla
        const labelBounds = this.calculateLabelBounds(
            dimensionId,
            screenPos,
            labelSize
        );

        // Collision check
        const collisions = this.config.avoidOverlap
            ? this.quadtree.query(labelBounds)
            : [];

        if (collisions.length === 0) {
            // Collision yok, orijinal pozisyon
            const rotation = this.config.autoRotateText
                ? this.calculateOptimalRotation(dimension, camera)
                : 0;

            // Label'ı kaydet
            this.labels.set(dimensionId, labelBounds);
            this.quadtree.insert(labelBounds);

            return {
                position: preferredPosition.clone(),
                rotation,
                offset: { x: 0, y: 0 },
                flipped: this.shouldFlip(rotation),
                score: 1
            };
        }

        // Alternatif pozisyon bul
        return this.findAlternativePosition(
            dimension,
            preferredPosition,
            labelBounds,
            labelSize,
            camera
        );
    }

    /**
     * Alternatif pozisyon bul (spiral search)
     */
    private findAlternativePosition(
        dimension: Dimension,
        originalPosition: THREE.Vector3,
        originalBounds: LabelBounds,
        labelSize: { width: number; height: number },
        camera: THREE.Camera
    ): LabelPlacement {
        const directions = this.getDirectionVectors();
        const { stepSize, maxSearchSteps } = this.config;

        let bestPlacement: LabelPlacement | null = null;
        let bestScore = -1;

        // Spiral search
        for (let step = 1; step <= maxSearchSteps; step++) {
            for (const dir of directions) {
                const offset = {
                    x: dir.x * stepSize * step,
                    y: dir.y * stepSize * step
                };

                const testBounds = this.offsetBounds(originalBounds, offset);

                // Ekran sınırları kontrolü
                if (!this.isInViewport(testBounds)) continue;

                const collisions = this.quadtree.query(testBounds);

                if (collisions.length === 0) {
                    // Burada collision yok
                    const worldPos = this.screenToWorld(
                        {
                            x: originalBounds.center.x + offset.x,
                            y: originalBounds.center.y + offset.y
                        },
                        originalPosition.z,
                        camera
                    );

                    const rotation = this.config.autoRotateText
                        ? this.calculateOptimalRotation(dimension, camera)
                        : 0;

                    // Skor hesapla (merkeze yakınlık)
                    const distanceFromOriginal = Math.sqrt(offset.x * offset.x + offset.y * offset.y);
                    const score = 1 - (distanceFromOriginal / (stepSize * maxSearchSteps));

                    if (score > bestScore) {
                        bestScore = score;
                        bestPlacement = {
                            position: worldPos,
                            rotation,
                            offset,
                            flipped: this.shouldFlip(rotation),
                            score
                        };
                    }

                    // İlk uygun pozisyonu hemen kullan (performans için)
                    if (step <= 3) {
                        const dimensionId = dimension.userData.dimensionId;
                        this.labels.set(dimensionId, testBounds);
                        this.quadtree.insert(testBounds);
                        return bestPlacement;
                    }
                }
            }
        }

        // En iyi pozisyonu kullan veya orijinale dön
        if (bestPlacement) {
            const dimensionId = dimension.userData.dimensionId;
            const finalBounds = this.offsetBounds(originalBounds, bestPlacement.offset);
            this.labels.set(dimensionId, finalBounds);
            this.quadtree.insert(finalBounds);
            return bestPlacement;
        }

        // Hiçbir yer bulunamadı, orijinale dön
        return {
            position: originalPosition.clone(),
            rotation: this.config.autoRotateText
                ? this.calculateOptimalRotation(dimension, camera)
                : 0,
            offset: { x: 0, y: 0 },
            flipped: false,
            score: 0
        };
    }

    /**
     * Yön vektörleri (tercih sırasına göre)
     */
    private getDirectionVectors(): { x: number; y: number }[] {
        const dirMap: Record<string, { x: number; y: number }> = {
            'up': { x: 0, y: -1 },
            'down': { x: 0, y: 1 },
            'left': { x: -1, y: 0 },
            'right': { x: 1, y: 0 }
        };

        const directions: { x: number; y: number }[] = [];

        // Tercih edilen yönler
        for (const dir of this.config.preferredDirections) {
            if (dirMap[dir]) {
                directions.push(dirMap[dir]);
            }
        }

        // Çapraz yönler
        directions.push(
            { x: 1, y: -1 },   // Sağ-Yukarı
            { x: -1, y: -1 },  // Sol-Yukarı
            { x: 1, y: 1 },    // Sağ-Aşağı
            { x: -1, y: 1 }    // Sol-Aşağı
        );

        return directions;
    }

    /**
     * Optimal rotation hesapla (okunaklılık için)
     */
    private calculateOptimalRotation(dimension: Dimension, camera: THREE.Camera): number {
        // Dimension çizgisi yönünü al
        const direction = this.getDimensionDirection(dimension);

        if (!direction) return 0;

        // Screen space'e çevir
        const screenDir = this.projectDirectionToScreen(direction, camera);

        // Açı hesapla
        let angle = Math.atan2(screenDir.y, screenDir.x);

        // Yazıyı okunaklı tut (baş aşağı olmasın)
        if (this.config.keepTextUpright) {
            if (angle > Math.PI / 2) angle -= Math.PI;
            if (angle < -Math.PI / 2) angle += Math.PI;
        }

        return angle;
    }

    /**
     * Dimension yönünü al
     */
    private getDimensionDirection(dimension: Dimension): THREE.Vector3 | null {
        // Dimension'dan referans noktalarını al
        const refs = (dimension as any).references;

        if (!refs || refs.length < 2) return null;

        const pointA = refs[0]?.point;
        const pointB = refs[1]?.point;

        if (!pointA || !pointB) return null;

        return this._tempVec3.subVectors(pointB, pointA).normalize();
    }

    /**
     * Yönü screen space'e project et
     */
    private projectDirectionToScreen(
        direction: THREE.Vector3,
        camera: THREE.Camera
    ): { x: number; y: number } {
        // Origin ve direction noktalarını project et
        const origin = new THREE.Vector3(0, 0, 0);
        const end = direction.clone();

        origin.project(camera);
        end.project(camera);

        return {
            x: end.x - origin.x,
            y: -(end.y - origin.y) // Y ters
        };
    }

    /**
     * World -> Screen dönüşümü
     */
    private worldToScreen(
        position: THREE.Vector3,
        camera: THREE.Camera
    ): { x: number; y: number } {
        const pos = position.clone().project(camera);

        return {
            x: (pos.x + 1) / 2 * this.viewport.width,
            y: (-pos.y + 1) / 2 * this.viewport.height
        };
    }

    /**
     * Screen -> World dönüşümü
     */
    private screenToWorld(
        screenPos: { x: number; y: number },
        depth: number,
        camera: THREE.Camera
    ): THREE.Vector3 {
        const x = (screenPos.x / this.viewport.width) * 2 - 1;
        const y = -(screenPos.y / this.viewport.height) * 2 + 1;

        const pos = new THREE.Vector3(x, y, 0.5);
        pos.unproject(camera);

        // Derinlik için ray ile kesişim
        const dir = pos.sub(camera.position).normalize();
        const distance = (depth - camera.position.z) / dir.z;

        return camera.position.clone().add(dir.multiplyScalar(distance));
    }

    /**
     * Label bounds hesapla
     */
    private calculateLabelBounds(
        id: string,
        screenPos: { x: number; y: number },
        labelSize: { width: number; height: number }
    ): LabelBounds {
        const halfWidth = labelSize.width / 2;
        const halfHeight = labelSize.height / 2;

        return {
            id,
            min: { x: screenPos.x - halfWidth, y: screenPos.y - halfHeight },
            max: { x: screenPos.x + halfWidth, y: screenPos.y + halfHeight },
            center: { ...screenPos },
            width: labelSize.width,
            height: labelSize.height
        };
    }

    /**
     * Bounds'u offset ile kaydır
     */
    private offsetBounds(bounds: LabelBounds, offset: { x: number; y: number }): LabelBounds {
        return {
            id: bounds.id,
            min: { x: bounds.min.x + offset.x, y: bounds.min.y + offset.y },
            max: { x: bounds.max.x + offset.x, y: bounds.max.y + offset.y },
            center: { x: bounds.center.x + offset.x, y: bounds.center.y + offset.y },
            width: bounds.width,
            height: bounds.height
        };
    }

    /**
     * Viewport içinde mi?
     */
    private isInViewport(bounds: LabelBounds): boolean {
        const margin = 10;
        return (
            bounds.min.x >= margin &&
            bounds.min.y >= margin &&
            bounds.max.x <= this.viewport.width - margin &&
            bounds.max.y <= this.viewport.height - margin
        );
    }

    /**
     * Flip gerekiyor mu?
     */
    private shouldFlip(rotation: number): boolean {
        // Yazı ters dönmüşse flip et
        return Math.abs(rotation) > Math.PI / 2;
    }

    /**
     * Label kaldır
     */
    removeLabel(dimensionId: string): void {
        this.labels.delete(dimensionId);

        // Quadtree'yi yeniden oluştur
        this.rebuildQuadtree();
    }

    /**
     * Quadtree'yi yeniden oluştur
     */
    private rebuildQuadtree(): void {
        this.quadtree.clear();
        this.labels.forEach(label => {
            this.quadtree.insert(label);
        });
    }

    /**
     * Tüm label'ları temizle
     */
    clear(): void {
        this.labels.clear();
        this.quadtree.clear();
    }

    /**
     * Label sayısı
     */
    getLabelCount(): number {
        return this.labels.size;
    }

    /**
     * Etkin mi?
     */
    isEnabled(): boolean {
        return this.config.enabled;
    }

    /**
     * Etkinleştir/Devre dışı bırak
     */
    setEnabled(enabled: boolean): void {
        this.config.enabled = enabled;
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export type { SmartPlacementConfig };
